import {
  ChangeDetectionStrategy,
  Component,
  inject,
  output,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { AppRegistryService } from '../../../core/services/app-registry.service';
import { UserPrefsService } from '../../../core/services/user-prefs.service';

@Component({
  selector: 'ec-waffle-menu',
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="waffle border border-gray-300"
      role="menu"
      aria-label="All apps"
    >
      <div
        class="waffle__header"
        style="display: flex; align-items: center; gap: 8px;"
      >
        All Apps
      </div>
      <div class="waffle__grid">
        @for (app of apps; track app.id) {
          <a
            class="waffle__item"
            [routerLink]="app.route"
            (click)="close.emit()"
            role="menuitem"
          >
            <img
              [src]="app.iconPath"
              [alt]="app.label"
              width="30"
              height="30"
              class="waffle__icon"
            />
            <span class="waffle__label">{{ app.label }}</span>
          </a>
        }
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        position: absolute;
        top: calc(100% + 8px);
        left: 0;
        z-index: 99999;
        display: block;
      }

      .waffle {
        width: 320px;
        background: var(--surface-bg);

        border-radius: 8px;
        box-shadow: var(--shadow-dropdown);
        overflow: hidden;
        padding-bottom: 8px;
      }

      .waffle__header {
        padding: 14px 16px 10px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--text-muted);
        border-bottom: 1px solid var(--surface-border);
        margin-bottom: 8px;
      }

      .waffle__grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 4px;
        padding: 0 8px;
      }

      .waffle__item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 14px 8px 12px;
        border-radius: var(--radius-lg);
        text-decoration: none;
        transition: background var(--transition-fast);

        &:hover {
          background: var(--surface-hover);
        }
      }

      .waffle__icon {
        display: block;
        flex-shrink: 0;
      }

      .waffle__label {
        font-size: 11px;
        font-weight: 500;
        color: var(--text-secondary);
        text-align: center;
        line-height: 1.3;
        word-break: break-word;
        hyphens: auto;
      }
    `,
  ],
})
export class WaffleMenuComponent {
  readonly close = output<void>();

  private readonly registry = inject(AppRegistryService);
  private readonly prefs = inject(UserPrefsService);

  get apps() {
    return this.prefs.orderedBuiltInApps();
  }
}
